import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { get } from 'lodash';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Thumbnail } from '../shared/thumbnail-utils';
import { ChapterModel } from '../story/models/chapter.model';
import { StoryChapterThumbnail } from '../story/models/story-chapter-thumbnail.model';
import { imageSrcToBlob } from './image-utils';
import { ChapterImage } from './models/chapter-image.model';
import { ImageUrls } from './models/image-urls.model';

declare const localBaseDirectory: string;

@Injectable({
  providedIn: 'root',
})
export class ImagesService {
  constructor(private http: HttpClient) {}

  // Iterate over a series of chapters and generate fresh images for saving
  // Most commonly used when copying a bookmark or saving over a modal
  generateFreshChapterImages(chapters: ChapterModel[], storyId: string, force = false) {
    const thumbnailRequests$: Observable<StoryChapterThumbnail>[] = [];

    // For each chapter, load check the thumbnail property
    // If a chapter is using legacy thumbnails, or the user is editing a module to turn into a bookmark, load thumbnails into memory for re-saving
    chapters.forEach((chapter: ChapterModel) => {
      const thumbnail = Thumbnail.getChapterThumbnail(chapter, storyId);

      if (thumbnail.legacy || force) {
        thumbnailRequests$.push(
          Thumbnail.makeFreshThumbnail(
            thumbnail.storyId,
            thumbnail.chapterId,
            thumbnail.urls.large,
          ),
        );
      }
    });

    // Register loaded thumbnails so that they re-upload during save
    return thumbnailRequests$.length ? forkJoin(thumbnailRequests$) : of([]);
  }

  // save thumbnail data for each chapter in a bookmark
  saveChapterThumbnails(
    chapters: any[],
    thumbnails: Map<string, StoryChapterThumbnail>,
    chapterIdMap: { [id: string]: string },
  ): Observable<ChapterImage>[] {
    // chapter thumbnails
    const thumbnailRequests$ = chapters.reduce<Observable<any>[]>((requests, chapter) => {
      const newChapterId = chapter.id;
      const oldChapterId = chapterIdMap[newChapterId];
      const currentThumbnail = thumbnails.get(oldChapterId);
      const snapshot = get(currentThumbnail, 'snapshot');

      if (snapshot) {
        requests.push(this.saveNewChapterImage(chapter.id, snapshot));
      }

      return requests;
    }, []);

    return thumbnailRequests$;
  }

  // Saves a chapter image as a new chapter image and maps the result to the chapter id
  // Achieved by loading the image into memory, posting the blob, and returning the new URL
  saveNewChapterImage(chapterId: string, imageSrc: string): Observable<ChapterImage> {
    return new Observable<ChapterImage>((observer) => {
      // Turn url into canvas
      imageSrcToBlob(imageSrc)
        .pipe(
          // Save blob to server
          switchMap((blob: Blob) => {
            return this.saveImageBlob(blob);
          }),

          // Record the new image URLs
          map((response: { uris: ImageUrls }) => {
            return {
              chapterId: chapterId,
              ...response,
            };
          }),
        )
        .subscribe(
          (value) => {
            observer.next(value);
            observer.complete();
          },
          () => {
            observer.next(null);
            observer.complete();
          },
        );
    });
  }

  getIndexThumbnail(id: string, size: string = 'large') {
    const directory = typeof localBaseDirectory === 'undefined' ? '' : localBaseDirectory;
    return `${directory}thumbs/modules/${id}/${size}/index.jpg`;
  }

  saveImageBlob(blob: Blob) {
    const imageUploadUrl = '/ws/media/images/upload';
    const file = new File([blob], 'image.jpg', { type: 'image/jpeg' });
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post(imageUploadUrl, formData) as Observable<{ uris: ImageUrls }>;
  }
}
